<template>
  <div>
    <v-container fluid style="max-width: none !important; width: 100%;">
      <!-- Header -->
      <v-skeleton-loader :loading="!flagNav" transition="scale-transition" max-height="64" type="table-heading">
        <v-toolbar dark class="header-title text-white">
          <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">Lexicon Management</v-toolbar-title>
          <v-spacer />
          <div class="d-flex align-center gap-3 pr-4">
            <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>

      <v-card class="card2" style="margin-top: 1rem">
        <v-container style="max-width: none !important; width: 100%;" class="px-6 py-6">
          <!-- Version Info -->
          <v-alert type="info" variant="tonal" class="mb-4">
            <div class="d-flex align-center justify-space-between">
              <div>
                <strong>Current Version:</strong> {{ currentVersion }}
                <span class="ml-4"><strong>Last Update:</strong> {{ lastUpdateDate }}</span>
              </div>
              <div class="d-flex align-center gap-2">
                <v-chip v-if="hasChanges" color="warning" size="small">
                  <v-icon start size="small">mdi-pencil</v-icon>
                  Unsaved Changes
                </v-chip>
                <v-btn v-if="permissions.edit || permissions.add || permissions.delete" class="btn-save" :loading="saving" @click="saveAllChanges" :disabled="!hasChanges">
                  <v-icon start>mdi-content-save</v-icon>
                  Save Changes
                </v-btn>
              </div>
            </div>
          </v-alert>

          <!-- Language Tabs -->
          <v-tabs v-model="activeTab" bg-color="transparent" color="primary" class="mb-4">
            <v-tab value="th">
              {{ languageItems[0].text }}
              <v-badge :content="getLanguageCount('th')" color="primary" inline />
            </v-tab>
            <v-tab value="en">
              {{ languageItems[1].text }}
              <v-badge :content="getLanguageCount('en')" color="primary" inline />
            </v-tab>
            <v-tab value="zh">
              {{ languageItems[2].text }}
              <v-badge :content="getLanguageCount('zh')" color="primary" inline />
            </v-tab>
          </v-tabs>

          <v-window v-model="activeTab">
            <!-- Thai Tab -->
            <v-window-item value="th">
              <lexicon-list
                language="th"
                :items="getLanguageItems('th')"
                @add="addItem('th')"
                @edit="editItem"
                @delete="deleteItem"
              />
            </v-window-item>

            <!-- English Tab -->
            <v-window-item value="en">
              <lexicon-list
                language="en"
                :items="getLanguageItems('en')"
                @add="addItem('en')"
                @edit="editItem"
                @delete="deleteItem"
              />
            </v-window-item>

            <!-- Chinese Tab -->
            <v-window-item value="zh">
              <lexicon-list
                language="zh"
                :items="getLanguageItems('zh')"
                @add="addItem('zh')"
                @edit="editItem"
                @delete="deleteItem"
              />
            </v-window-item>
          </v-window>
        </v-container>
      </v-card>
    </v-container>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="600px">
      <v-card style="border-radius: 18px !important;">
        <v-card-title class="dialog-header dialog-header-add">
          <div class="d-flex align-center">
            <div class="header-icon-container">
              <v-avatar class="icon-background" size="46">
                <v-icon color="#4b2667" size="24">
                  {{ editMode ? 'mdi-pencil' : 'mdi-plus' }}
                </v-icon>
              </v-avatar>
            </div>
            <div>
              <div class="dialog-title">
                {{ editMode ? 'Edit Lexicon' : 'Add Lexicon' }}
              </div>
              <div class="dialog-subtitle">
                {{ getLanguageName(formData.language) }}
              </div>
            </div>
          </div>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" @click="closeDialog" />
        </v-card-title>

        <v-card-text class="pt-6">
          <v-form ref="form" v-model="valid">
            <v-row>
              <v-col cols="12">
                <v-text-field 
                  v-model="formData.original" 
                  label="Original Text *" 
                  variant="outlined" 
                  :rules="[rules.required, rules.maxLength, rules.noDuplicate]" 
                  :maxlength="255"
                  counter
                  autofocus 
                />
              </v-col>
              <v-col cols="12">
                <v-text-field 
                  v-model="formData.read" 
                  label="Pronunciation *" 
                  variant="outlined" 
                  :rules="[rules.required, rules.maxLength]" 
                  :maxlength="255"
                  counter
                />
              </v-col>
              <v-col cols="12">
                <v-switch v-model="formData.activeFlag" label="Active" color="primary" inset hide-details />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions class="px-6 pb-6">
          <v-spacer />
          <v-btn style="margin-right: 15px;border-radius: 12px; height: 50px; width: 90px;" variant="outlined" @click="closeDialog">Cancel</v-btn>
          <v-btn v-if="editMode ? permissions.edit : permissions.add" class="btn-layout" @click="saveItem">{{ editMode ? 'Update' : 'Add' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="500px">
      <v-card style="border-radius: 18px !important;">
        <v-card-title class="dialog-header dialog-header-delete">
          <div class="d-flex align-center">
            <div class="header-icon-container">
              <v-avatar class="icon-background-delete" size="46">
                <v-icon color="#d32f2f" size="24">mdi-delete</v-icon>
              </v-avatar>
            </div>
            <div>
              <div class="dialog-title">Confirm Delete</div>
              <div class="dialog-subtitle">This will be deleted when you save</div>
            </div>
          </div>
        </v-card-title>

        <v-card-text class="pt-4">
          <p>Are you sure you want to delete this lexicon entry?</p>
          <v-alert type="warning" variant="tonal" class="mt-4">
            <strong>Original:</strong> {{ itemToDelete?.original }}<br />
            <strong>Pronunciation:</strong> {{ itemToDelete?.read }}<br />
            <strong>Language:</strong> {{ getLanguageName(itemToDelete?.language) }}
          </v-alert>
        </v-card-text>

        <v-card-actions class="px-6 pb-6">
          <v-spacer />
          <v-btn style="margin-right: 15px;border-radius: 12px; height: 50px; width: 90px;" @click="deleteDialog = false">Cancel</v-btn>
          <v-btn v-if="permissions.delete" style="margin-right: 15px;border-radius: 12px; height: 50px; width: 90px;" color="error" @click="confirmDeleteItem">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" location="top">
      {{ snackbarText }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Cookies from 'js-cookie';
import EditImg from '@/assets/edit1.png';
import DeleteImg from '@/assets/delete1.png';

// Lexicon List Component
const LexiconList = {
  name: 'LexiconList',
  props: {
    language: String,
    items: Array,
  },
  emits: ['add', 'edit', 'delete'],
  data() {
    return {
      search: '',
      headers: [
        { title: 'Original', key: 'original', sortable: true },
        { title: 'Pronunciation', key: 'read', sortable: true },
        { title: 'Status', key: 'activeFlag', sortable: true, align: 'center' },
        { title: 'Actions', key: 'actions', sortable: false, align: 'center' },
      ],
      images: {
        EditImg: EditImg,
        DeleteImg: DeleteImg,
      },
    };
  },
  computed: {
    isViewOnly() {
      return this.$pagePermission && this.$pagePermission.isViewOnly('LexiconManagement');
    },
    canAdd() {
      return !!(this.$pagePermission && this.$pagePermission.canAdd('LexiconManagement'));
    },
    canEdit() {
      return !!(this.$pagePermission && this.$pagePermission.canEdit('LexiconManagement'));
    },
    canDelete() {
      return !!(this.$pagePermission && this.$pagePermission.canDelete('LexiconManagement'));
    },
    permissions() {
      return {
        add: this.$pagePermission && this.$pagePermission.canAdd('LexiconManagement'),
        edit: this.$pagePermission && this.$pagePermission.canEdit('LexiconManagement'),
        delete: this.$pagePermission && this.$pagePermission.canDelete('LexiconManagement'),
      }
    },
    filteredItems() {
      if (!this.search) return this.items;
      const searchLower = this.search.toLowerCase();
      return this.items.filter(
        (item) => item.original?.toLowerCase().includes(searchLower) || item.read?.toLowerCase().includes(searchLower)
      );
    },
  },
  template: `
    <div>
      <v-row class="mb-4">
        <v-col cols="12" md="6">
          <v-text-field
            v-model="search"
            placeholder="Search"
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            rounded
            clearable
            hide-details
          />
        </v-col>
        <v-col v-if="permissions.add" cols="12" md="6" class="d-flex justify-end align-center">
          <v-btn
            class="btn-layout"
            style="padding: 14px 24px; width: 150px; height: 50px; background: linear-gradient(90deg, #504897 0%, #4b2667 100%); border-radius: 16px; color: #fff; text-transform: none; font-weight: 500; letter-spacing: 0.5px;"
            @click="$emit('add')"
          >
            <v-icon start size="20">mdi-plus-circle</v-icon>
            Add Entry
          </v-btn>
        </v-col>
      </v-row>

      <div class="lexicon-table-container">
      <v-data-table
        :headers="headers"
        :items="filteredItems"
        class="elevation-1 my-data-table modern-table"
        density="comfortable"
        :items-per-page="-1"
        fixed-header
        height="500"
      >
        <template v-slot:item.activeFlag="{ item }">
          <v-chip :color="item.activeFlag ? 'success' : 'error'" size="small" variant="outlined">
            {{ item.activeFlag ? 'Active' : 'Inactive' }}
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <div v-if="permissions.edit || permissions.delete">
            <v-btn v-if="permissions.edit" style="margin-right: 10px; height: 36px; width: 36px; border-radius: 6px;" size="small" variant="outlined" color="primary" @click="$emit('edit', item)">
              <v-img :src="images.EditImg" width="14" height="14" />
              <v-tooltip activator="parent" location="top">Edit</v-tooltip>
            </v-btn>
            <v-btn v-if="permissions.delete" style="height: 36px; width: 36px; border-radius: 6px;" size="small" variant="outlined" color="error" @click="$emit('delete', item)">
              <v-img :src="images.DeleteImg" width="14" height="14" />
              <v-tooltip activator="parent" location="top">Delete</v-tooltip>
            </v-btn>
          </div>
        </template>

        <template v-slot:no-data>
          <div class="text-center py-8">
            <v-icon size="64" color="grey">mdi-book-alphabet</v-icon>
            <p class="text-h6 mt-4">No entries yet</p>
            <p class="text-body-2 text-grey">Click "Add Entry" to create your first lexicon entry</p>
          </div>
        </template>
      </v-data-table>
      </div>
    </div>
  `,
};

export default {
  name: 'LexiconManagement',
  components: {
    LexiconList,
  },
  data() {
    return {
      // UI State
      today: null,
      flagNav: true,
      loading: false,
      saving: false,
      dialog: false,
      deleteDialog: false,
      editMode: false,
      valid: false,

      // Active Tab
      activeTab: 'th',

      // Version Info
      currentVersion: 0,
      lastUpdateDate: '-',

      // Data
      items: [], // Original data from server
      pendingChanges: {
        create: [],
        update: [],
        delete: [],
      },

      // Current item being edited/deleted
      itemToDelete: null,

      // Form Data
      formData: {
        lexiconID: null,
        language: '',
        original: '',
        read: '',
        activeFlag: true,
        _isNew: false, // Track if this is a new item
        _tempId: null, // Temporary ID for new items
      },

      // Options
      languageItems: [
        { text: 'Thai (ไทย)', value: 'th' },
        { text: 'English', value: 'en' },
        { text: 'Chinese (中文)', value: 'zh' },
      ],

      // Validation Rules
      rules: {
        required: (value) => !!value || 'This field is required',
        maxLength: (value) => !value || value.length <= 255 || 'Maximum 255 characters',
        noDuplicate: (value) => this.checkDuplicateOriginal(value),
      },

      // Snackbar
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',

      // Images
      images: {
        EditImg: EditImg,
        DeleteImg: DeleteImg,
      },

      // Temporary ID counter for new items
      _tempIdCounter: 0,
    };
  },

  computed: {
    FontColorTitle() {
      return Cookies.get('fontColorTitle') || 'FFFFFF';
    },

    canAdd() {
      return !!(this.$pagePermission && this.$pagePermission.canAdd('LexiconManagement'));
    },
    canEdit() {
      return !!(this.$pagePermission && this.$pagePermission.canEdit('LexiconManagement'));
    },
    canDelete() {
      return !!(this.$pagePermission && this.$pagePermission.canDelete('LexiconManagement'));
    },
    permissions() {
      return {
        add: this.$pagePermission && this.$pagePermission.canAdd('LexiconManagement'),
        edit: this.$pagePermission && this.$pagePermission.canEdit('LexiconManagement'),
        delete: this.$pagePermission && this.$pagePermission.canDelete('LexiconManagement'),
      }
    },

    hasChanges() {
      return this.pendingChanges.create.length > 0 || this.pendingChanges.update.length > 0 || this.pendingChanges.delete.length > 0;
    },

    allItems() {
      
      // Combine original items with pending changes
      let result = [...this.items];

      // Add new items
      result = result.concat(this.pendingChanges.create);

      // Apply updates
      this.pendingChanges.update.forEach((updatedItem) => {
        let foundIndex = -1;
        for (let i = 0; i < result.length; i++) {
          if (result[i].lexiconID === updatedItem.lexiconID) {
            foundIndex = i;
            break;
          } else if (result[i]._tempId && result[i]._tempId === updatedItem._tempId) {
            foundIndex = i;
            break;
          }
        }
        if (foundIndex !== -1) {
          result[foundIndex] = { ...updatedItem };
        } else {
          console.log('NOT FOUND! Item will not be updated');
        }
      });

      // Remove deleted items
      result = result.filter((item) => {
        return !this.pendingChanges.delete.some((deletedItem) => 
          (deletedItem.lexiconID && deletedItem.lexiconID === item.lexiconID) || 
          (deletedItem._tempId && deletedItem._tempId === item._tempId)
        );
      });

      return result;
    },

    apiBaseUrl() {
      return import.meta.env.VITE_APP_API;
    },
  },

  mounted() {
    this.GetDate();
    this.loadLexicons();
    this.loadVersion();
  },

  methods: {
    GetDate() {
      let today1 = new Date();
      let dd = today1.getDate();
      let mm = today1.getMonth() + 1;
      let yyyy = today1.getFullYear();
      if (dd < 10) {
        dd = '0' + dd;
      }
      if (mm < 10) {
        mm = '0' + mm;
      }
      this.today = dd + '/' + mm + '/' + yyyy;
    },
    // Helper Methods
    getLanguageName(language) {
      const map = {
        th: 'Thai',
        en: 'English',
        zh: 'Chinese',
      };
      return map[language] || language;
    },

    getLanguageColor(language) {
      const map = {
        th: 'blue',
        en: 'green',
        zh: 'orange',
      };
      return map[language] || 'grey';
    },

    getLanguageItems(language) {
      return this.allItems.filter((item) => item.language === language);
    },

    getLanguageCount(language) {
      return this.getLanguageItems(language).length;
    },

    showSnackbar(text, color = 'success') {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },

    // Duplicate Validation
    checkDuplicateOriginal(value) {
      if (!value) return true;
      
      const trimmedValue = value.trim();
      const language = this.formData.language;
      const currentTempId = this.formData._tempId;
      const currentId = this.formData.lexiconID;
      
      // Check in allItems (combined server data + pending changes)
      const duplicate = this.allItems.find(item => {
        // Skip current item being edited
        if (currentId && item.lexiconID === currentId) return false;
        if (currentTempId && item._tempId === currentTempId) return false;
        
        // Check if same language and same original (after trim, case-sensitive)
        return item.language === language && item.original?.trim() === trimmedValue;
      });
      
      return !duplicate || `"${trimmedValue}" already exists in this language`;
    },

    // Data Loading
    async loadLexicons() {
      this.loading = true;
      try {
        this.$call.GetLexicons(
          null, true,
          (dataResponse) => {
            if (dataResponse.status === 0) {
              this.items = dataResponse.data || [];
              this.resetChanges();
            } else {
              this.items = [];
              this.showSnackbar('Failed to load lexicons', 'error');
            }
            this.loading = false;
          },
          (errorResource) => {
            this.loading = false;
            this.showSnackbar('Error loading lexicons', 'error');
            console.error(errorResource);
          }
        );
      } catch (error) {
        this.loading = false;
        this.showSnackbar('Error loading lexicons', 'error');
        console.error(error);
      }
    },

    async loadVersion() {
      try {
        this.$call.GetLexiconVersion(
          (dataResponse) => {
            if (dataResponse.status === 0) {
              const data = dataResponse.data;
              this.currentVersion = data.latestVersion || 0;
              this.lastUpdateDate = data.lastUpdateDate ? new Date(data.lastUpdateDate).toLocaleString() : '-';
            }
          },
          (errorResource) => {
            console.error('Error loading version:', errorResource);
          }
        );
      } catch (error) {
        console.error('Error loading version:', error);
      }
    },

    async refreshData() {
      if (this.hasChanges) {
        const confirm = await this.$swal.fire({
          title: 'Unsaved Changes',
          text: 'You have unsaved changes. Refreshing will discard them. Continue?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, Refresh',
          cancelButtonText: 'Cancel',
        });
        if (!confirm.isConfirmed) return;
      }
      await this.loadLexicons();
      await this.loadVersion();
      this.showSnackbar('Data refreshed', 'info');
    },

    resetChanges() {
      this.pendingChanges = {
        create: [],
        update: [],
        delete: [],
      };
    },

    // CRUD Operations (Local)
    addItem(language) {
      this.editMode = false;
      this._tempIdCounter++;
      this.formData = {
        lexiconID: null,
        language: language,
        original: '',
        read: '',
        activeFlag: true,
        _isNew: true,
        _tempId: `temp_${this._tempIdCounter}`,
      };
      this.dialog = true;
    },

    editItem(item) {
      this.editMode = true;
      this.formData = { ...item };
      this.dialog = true;
    },

    deleteItem(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },

    saveItem() {
      if (!this.$refs.form.validate()) return;

      // Create a clean copy of formData with trimmed original
      const itemData = {
        lexiconID: this.formData.lexiconID,
        language: this.formData.language,
        original: this.formData.original?.trim() || '',
        read: this.formData.read,
        activeFlag: this.formData.activeFlag,
        _isNew: this.formData._isNew,
        _tempId: this.formData._tempId
      };

      if (itemData._isNew) {
        // Check if this new item is already in pending creates (editing a new item)
        const createIndex = this.pendingChanges.create.findIndex(
          (item) => item._tempId === itemData._tempId
        );
        
        if (createIndex !== -1) {
          // Update existing item in pending creates
          this.pendingChanges.create[createIndex] = itemData;
        } else {
          // Add new item to pending creates
          this.pendingChanges.create.push(itemData);
        }
        this.showSnackbar('Entry added (not saved yet)', 'info');
      } else {
        // Editing an existing item from server
        const updateIndex = this.pendingChanges.update.findIndex(
          (item) => item.lexiconID === itemData.lexiconID
        );
        
        if (updateIndex !== -1) {
          // Update existing pending update
          this.pendingChanges.update[updateIndex] = itemData;
        } else {
          // Add new pending update
          this.pendingChanges.update.push(itemData);
        }
        this.showSnackbar('Entry updated (not saved yet)', 'info');
      }

      this.closeDialog();
    },

    confirmDeleteItem() {
      if (!this.itemToDelete) return;

      // Check if this is a new item (not yet saved to server)
      if (this.itemToDelete._isNew) {
        // Remove from pending creates
        this.pendingChanges.create = this.pendingChanges.create.filter(
          (item) => item._tempId !== this.itemToDelete._tempId
        );
      } else {
        // Add to pending deletes
        this.pendingChanges.delete.push(this.itemToDelete);
        
        // Remove from pending updates if exists
        this.pendingChanges.update = this.pendingChanges.update.filter(
          (item) => item.lexiconID !== this.itemToDelete.lexiconID
        );
      }

      this.showSnackbar('Entry marked for deletion (not saved yet)', 'info');
      this.deleteDialog = false;
      this.itemToDelete = null;
    },

    // Save All Changes
    async saveAllChanges() {
      if (!this.hasChanges) {
        this.showSnackbar('No changes to save', 'info');
        return;
      }

      // Confirm before saving
      const confirm = await this.$swal.fire({
        title: 'Save All Changes?',
        html: `
          <div style="text-align: left;">
            <p style="margin-bottom: 4px;"><strong>Create:</strong> ${this.pendingChanges.create.length} entries</p>
            <p style="margin-bottom: 4px;"><strong>Update:</strong> ${this.pendingChanges.update.length} entries</p>
            <p style="margin-bottom: 4px;"><strong>Delete:</strong> ${this.pendingChanges.delete.length} entries</p>
            <hr/>
            <p style="color: #666;margin-top: 4px;">This will also increment the version number.</p>
          </div>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, Save All',
        cancelButtonText: 'Cancel',
      });

      if (!confirm.isConfirmed) return;

      this.saving = true;

      try {
        // Prepare bulk save request
        const bulkRequest = {
          create: this.pendingChanges.create.map(item => ({
            language: item.language,
            original: item.original,
            read: item.read,
            activeFlag: item.activeFlag !== undefined ? item.activeFlag : true,
          })),
          update: this.pendingChanges.update.map(item => ({
            lexiconID: item.lexiconID,
            language: item.language,
            original: item.original,
            read: item.read,
            activeFlag: item.activeFlag !== undefined ? item.activeFlag : true,
          })),
          delete: this.pendingChanges.delete.map(item => item.lexiconID),
        };

        // Send all changes in a single request
        this.$call.BulkSaveChanges(
          bulkRequest,
          async (dataResponse) => {
            if (dataResponse.status === 0) {
              const result = dataResponse.data;
              
              if (result.hasErrors) {
                // Show detailed error information
                let errorMsg = `Completed with errors:\n\n`;
                errorMsg += `✓ Created: ${result.created}\n`;
                errorMsg += `✓ Updated: ${result.updated}\n`;
                errorMsg += `✓ Deleted: ${result.deleted}\n\n`;
                errorMsg += `Errors:\n${result.errors.join('\n')}`;
                
                this.$swal.fire({
                  title: 'Partial Success',
                  text: errorMsg,
                  icon: 'warning',
                  confirmButtonText: 'OK'
                });
              } else {
                this.showSnackbar(
                  `Successfully saved! Created: ${result.created}, Updated: ${result.updated}, Deleted: ${result.deleted}`,
                  'success'
                );
              }
              
              // Reload data
              await this.loadLexicons();
              await this.loadVersion();
            } else {
              this.showSnackbar(dataResponse.message || 'Failed to save changes', 'error');
            }
            this.saving = false;
          },
          (errorResource) => {
            this.saving = false;
            this.showSnackbar('Error saving changes', 'error');
            console.error(errorResource);
          }
        );
      } catch (error) {
        this.saving = false;
        this.showSnackbar('Error saving changes', 'error');
        console.error(error);
      }
    },

    // Dialog Methods
    closeDialog() {
      this.dialog = false;
      this.formData = {
        lexiconID: null,
        language: '',
        original: '',
        read: '',
        activeFlag: true,
        _isNew: false,
        _tempId: null,
      };
      if (this.$refs.form) {
        this.$refs.form.reset();
      }
    },
  },
};
</script>

<style scoped>
.card2 {
  width: 100%;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.search-field {
  border-radius: 8px;
}

.btn-layout {
  padding: 14px 24px;
  width: 151px;
  height: 50px;
  background: linear-gradient(90deg, #504897 0%, #4b2667 100%) !important;
  border-radius: 16px;
  color: #fff !important;
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.btn-layout:hover {
  background: linear-gradient(90deg, #5a52a7 0%, #5b3077 100%) !important;
  box-shadow: 0 4px 12px rgba(75, 38, 103, 0.3);
}

.btn-category {
  background: linear-gradient(90deg, #1976d2 0%, #42a5f5 100%);
  color: white;
  text-transform: none;
  border-radius: 8px;
}

.action-buttons-group {
  display: flex;
  gap: 8px !important;
  justify-content: center;
}

.action-btn {
  min-width: 36px !important;
  width: 36px !important;
  height: 36px !important;
  border-radius: 6px;
}

.dialog-header {
  background: linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%);
  border-bottom: 1px solid #e0e0e0;
}

.dialog-header-add {
  background: linear-gradient(135deg, #f0e6f6 0%, #e8d5f0 100%);
}

.dialog-header-delete {
  background: linear-gradient(135deg, #ffcdd2 0%, #ffebee 100%);
}

.header-icon-container {
  margin-right: 16px;
}

.icon-background {
  background: linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%);
}

.icon-background-delete {
  background: linear-gradient(135deg, #ffffff 0%, #ffebee 100%);
}

.dialog-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.dialog-subtitle {
  font-size: 13px;
  color: #666;
  margin-top: 2px;
}

.json-example {
  background: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  font-size: 12px;
  overflow-x: auto;
}

.my-data-table {
  border-radius: 8px;
}

.btn-save {
  background: linear-gradient(90deg, #2e7d32 0%, #43a047 100%);
  color: white;
  text-transform: none;
  border-radius: 8px;
}

.btn-save:disabled {
  background: #ccc !important;
  color: #666 !important;
}

.gap-3 {
  gap: 12px;
}

.gap-2 {
  gap: 8px;
}

</style>

<!-- Non-scoped: ใช้เข้าถึง inline sub-component (LexiconList) ที่ scoped CSS ทะลุไม่ได้ -->
<style>
.lexicon-table-container .v-table__wrapper {
  scrollbar-width: thin;
  scrollbar-color: rgba(25, 118, 210, 0.3) rgba(0, 0, 0, 0.05);
}

.lexicon-table-container .v-table__wrapper thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: rgb(var(--v-theme-surface, 255, 255, 255));
}

.lexicon-table-container .v-table__wrapper::-webkit-scrollbar {
  width: 6px;
}

.lexicon-table-container .v-table__wrapper::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
}

.lexicon-table-container .v-table__wrapper::-webkit-scrollbar-thumb {
  background: rgba(25, 118, 210, 0.3);
  border-radius: 3px;
}

.lexicon-table-container .v-table__wrapper::-webkit-scrollbar-thumb:hover {
  background: rgba(25, 118, 210, 0.5);
}
</style>
